'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface CustomModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  closeOnOutsideClick?: boolean
  closeOnEsc?: boolean
  initialFocusRef?: React.RefObject<HTMLElement>
  className?: string
}

export default function CustumeModal({
  isOpen,
  onClose,
  children,
  title,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  initialFocusRef,
  className = 'max-w-3xl',
}: CustomModalProps) {
  const [mounted, setMounted] = useState<boolean>(false)
  const modalRootRef = useRef<HTMLDivElement | null>(null)
  const modalContentRef = useRef<HTMLDivElement>(null)
  const lastFocusedElementRef = useRef<Element | null>(null)

  // Initialize modal root and handle mounting
  useEffect(() => {
    // Create modal root if it doesn't exist
    modalRootRef.current = document.getElementById(
      'modal-root',
    ) as HTMLDivElement
    if (!modalRootRef.current) {
      modalRootRef.current = document.createElement('div')
      modalRootRef.current.id = 'modal-root'
      document.body.appendChild(modalRootRef.current)
    }
    setMounted(true)

    return () => {
      // Cleanup modal root if empty
      if (modalRootRef.current?.childNodes.length === 0) {
        document.body.removeChild(modalRootRef.current)
      }
    }
  }, [])

  // Handle focus management and event listeners
  useEffect(() => {
    if (!isOpen || !mounted) return

    lastFocusedElementRef.current = document.activeElement

    if (initialFocusRef?.current) {
      initialFocusRef.current.focus()
    } else {
      modalContentRef.current?.focus()
    }

    // Handle Escape key
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (closeOnEsc && e.key === 'Escape') onClose()
    }

    // Prevent body scrolling
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      // Restore focus and scrolling
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
      if (lastFocusedElementRef.current instanceof HTMLElement) {
        lastFocusedElementRef.current.focus()
      }
    }
  }, [isOpen, mounted, onClose, closeOnEsc, initialFocusRef])

  if (!isOpen || !mounted) return null

  const handleBackdropClick = (): void => {
    if (closeOnOutsideClick) {
      onClose()
    }
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation()
  }

  const handleCloseClick = (): void => {
    onClose()
  }

  if (!modalRootRef.current) return null

  return createPortal(
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-[#EAEAEA] p-4'
      onClick={handleBackdropClick}
    >
      <div
        ref={modalContentRef}
        className={`relative bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${className}`}
        onClick={handleContentClick}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? 'modal-title' : undefined}
        tabIndex={-1}
      >
        {/* Close button */}
        <button
          onClick={handleCloseClick}
          aria-label='Close modal'
          className='absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            className='h-6 w-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        {/* Modal content */}
        <div className='p-6'>
          {title && (
            <h2
              id='modal-title'
              className='text-2xl font-bold text-gray-900 mb-4'
            >
              {title}
            </h2>
          )}
          {children}
        </div>
      </div>
    </div>,
    modalRootRef.current,
  )
}
